/**
 * D-EZ-102 — "STORYLINES TO WATCH", the TV panel before the green.
 *
 * Built from what the league knew going into this race (`seasonContextBefore`)
 * and the week that set the grid: the defending winner here, who is hot, the
 * feuds in the field, the playoff bubble, milestones in play, and the track's
 * temper. Every line is a fact going in; nothing predicts the race.
 */

import { useMemo } from 'react';
import type { SeasonState } from '../../domain/seasonPlay.ts';
import type { RaceWeekResult } from '../../domain/raceWeek.ts';
import type { HistorySeason } from '../../domain/history/index.ts';
import { seasonContextBefore } from '../../domain/story/league.ts';
import { storylinesFor, type Storyline } from '../../domain/story/storylines.ts';
import { trackCard } from '../../domain/turn.ts';

export interface StorylinesPanelProps {
  storylines: readonly Storyline[];
  raceName?: string;
}

/** The panel itself — a list of short TV graphics. */
export function StorylinesPanel({ storylines, raceName }: StorylinesPanelProps) {
  if (storylines.length === 0) return null;
  return (
    <section className="storylines" aria-labelledby="storylines-h">
      <p className="storylines__kicker">{raceName ? `Before the green · ${raceName}` : 'Before the green'}</p>
      <h2 id="storylines-h" className="storylines__title">
        Storylines to watch
      </h2>
      <ul className="storylines__list">
        {storylines.map((s) => (
          <li key={`${s.kind}|${s.text}`} className={`storyline storyline--${s.kind}`}>
            <span className="storyline__tag">{s.title}</span>
            <span className="storyline__text">{s.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface RaceWeekStorylinesProps {
  /** The live season, BEFORE the race (as the race-week screen holds it). */
  state: SeasonState;
  seasonNumber: number;
  teams: HistorySeason['teams'];
  /** Settled seasons of this league, oldest first. */
  history?: readonly HistorySeason[];
  career?: HistorySeason | null;
  /** The week that set this race's grid, once it has been run. */
  week?: RaceWeekResult | null;
}

/**
 * The storylines for the next race of a live season. Self-contained so the
 * race-week screen needs one line to show it.
 */
export default function RaceWeekStorylines({ state, seasonNumber, teams, history = [], career = null, week = null }: RaceWeekStorylinesProps) {
  const scheduled = state.schedule.races[state.nextRace - 1];
  const storylines = useMemo(() => {
    if (!scheduled) return [];
    const ctx = seasonContextBefore(
      { history, career, live: { seasonNumber, roster: state.roster, teams, state } },
      state.nextRace,
    );
    let category: string | null = null;
    try {
      category = trackCard(scheduled.trackId).category;
    } catch {
      category = null;
    }
    const names = new Map(state.roster.map((c) => [c.driverId, c.name]));
    for (const c of week?.grid ?? []) names.set(c.driverId, c.name);
    return storylinesFor(ctx, {
      feuds: week?.feuds ?? [],
      nameOf: (id) => names.get(id) ?? id,
      weather: week?.badWeather ? 'Bad weather wiped out qualifying — the whole field lined up off the deck.' : null,
      trackCategory: category,
      trackPersonality: state.options.race?.trackPersonality !== false,
      packRacing: state.options.race?.packRacing !== false,
      fieldIds: week ? week.grid.map((c) => c.driverId) : undefined,
    });
  }, [scheduled, history, career, seasonNumber, state, teams, week]);
  return <StorylinesPanel storylines={storylines} raceName={scheduled?.raceName} />;
}
